import { Link, useLocation } from "react-router-dom";
import { useState, useEffect, useRef, useCallback } from "react";
import type { ReactNode } from "react";

export interface PublicLayoutProps {
  children: ReactNode;
  hideFooter?: boolean;
}

export function PublicLayout({
  children,
  hideFooter = false,
}: PublicLayoutProps) {
  return (
    <div className="public-layout">
      <a href="#main-content" className="skip-to-content">
        Skip to main content
      </a>
      <PublicHeader />
      <main id="main-content" className="public-main">
        {children}
      </main>
      {!hideFooter && <PublicFooter />}
    </div>
  );
}

export function PublicHeader() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const hamburgerRef = useRef<HTMLButtonElement>(null);
  const location = useLocation();

  // Close menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Lock body scroll when menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileMenuOpen]);

  // Focus trap inside mobile menu
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!mobileMenuOpen) return;

      if (e.key === "Escape") {
        setMobileMenuOpen(false);
        hamburgerRef.current?.focus();
        return;
      }

      if (e.key === "Tab" && menuRef.current) {
        const focusable = menuRef.current.querySelectorAll<HTMLElement>(
          'a, button, [tabindex]:not([tabindex="-1"])',
        );
        if (focusable.length === 0) return;

        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    },
    [mobileMenuOpen],
  );

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  // Focus the close button when menu opens
  useEffect(() => {
    if (mobileMenuOpen && menuRef.current) {
      const closeBtn =
        menuRef.current.querySelector<HTMLElement>(".mobile-nav__close");
      closeBtn?.focus();
    }
  }, [mobileMenuOpen]);

  return (
    <header className="public-header">
      <div className="public-header-content">
        <Link to="/" className="public-logo">
          AccessiMonitor
        </Link>
        <nav className="public-nav public-nav--desktop">
          <Link to="/how-it-works">How it works</Link>
          <Link to="/plans">Plans</Link>
          <Link to="/methodology">Methodology</Link>
          <Link to="/support">Support</Link>
          <Link to="/sign-in" className="btn btn--small">
            Sign in
          </Link>
        </nav>
        <button
          ref={hamburgerRef}
          type="button"
          className="hamburger-btn"
          onClick={() => setMobileMenuOpen(true)}
          aria-expanded={mobileMenuOpen}
          aria-controls="public-mobile-nav"
          aria-label="Open navigation menu"
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            aria-hidden="true"
          >
            <line x1="3" y1="6" x2="21" y2="6" />
            <line x1="3" y1="12" x2="21" y2="12" />
            <line x1="3" y1="18" x2="21" y2="18" />
          </svg>
        </button>
      </div>

      {mobileMenuOpen && (
        <div
          className="mobile-nav-backdrop"
          onClick={() => setMobileMenuOpen(false)}
          aria-hidden="true"
        />
      )}

      <div
        ref={menuRef}
        id="public-mobile-nav"
        className={`mobile-nav ${mobileMenuOpen ? "mobile-nav--open" : ""}`}
        role="dialog"
        aria-modal={mobileMenuOpen || undefined}
        aria-label="Navigation menu"
        aria-hidden={!mobileMenuOpen}
      >
        <div className="mobile-nav__header">
          <span className="mobile-nav__title">Menu</span>
          <button
            type="button"
            className="mobile-nav__close"
            onClick={() => {
              setMobileMenuOpen(false);
              hamburgerRef.current?.focus();
            }}
            aria-label="Close navigation menu"
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              aria-hidden="true"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
        <nav className="mobile-nav__links">
          <Link to="/how-it-works">How it works</Link>
          <Link to="/plans">Plans</Link>
          <Link to="/methodology">Methodology</Link>
          <Link to="/support">Support</Link>
          <Link to="/sign-in" className="btn btn--primary mobile-nav__cta">
            Sign in
          </Link>
        </nav>
      </div>
    </header>
  );
}

export function PublicFooter() {
  return (
    <footer className="public-footer">
      <div className="public-footer-content">
        <div className="footer-section">
          <h4>Product</h4>
          <ul>
            <li>
              <Link to="/how-it-works">How it works</Link>
            </li>
            <li>
              <Link to="/plans">Plans</Link>
            </li>
            <li>
              <Link to="/recurring-monitoring">Recurring monitoring</Link>
            </li>
            <li>
              <Link to="/document-accessibility">Document scanning</Link>
            </li>
          </ul>
        </div>
        <div className="footer-section">
          <h4>Resources</h4>
          <ul>
            <li>
              <Link to="/methodology">Methodology</Link>
            </li>
            <li>
              <Link to="/manual-review">Manual review</Link>
            </li>
            <li>
              <Link to="/statement-generator">Statement generator</Link>
            </li>
            <li>
              <Link to="/support">Support</Link>
            </li>
          </ul>
        </div>
        <div className="footer-section">
          <h4>Company</h4>
          <ul>
            <li>
              <Link to="/contact">Contact</Link>
            </li>
            <li>
              <Link to="/privacy">Privacy</Link>
            </li>
            <li>
              <Link to="/terms">Terms</Link>
            </li>
          </ul>
        </div>
      </div>
      <div className="footer-bottom">
        <p>
          &copy; AccessiMonitor {new Date().getFullYear()}. All rights reserved.
        </p>
      </div>
    </footer>
  );
}
